import React, { useEffect, useState } from 'react';
import { ref, uploadBytes, uploadBytesResumable, uploadString, getBytes, listAll, deleteObject } from 'firebase/storage';
import {
  HardDriveUpload, CheckCircle2, Trash2, RefreshCw, FileCode2, Upload, AlertTriangle, Layers, Wand2,
} from 'lucide-react';
import { storage } from '../firebase';
import {
  analizarBinario, construirImagenCompleta, verificarImagenCompleta, tamanoParticionApp,
} from '../lib/esp32Firmware';

// Gestión del firmware del ESP32 desde el panel de administración.
//
// Estructura en Storage:
//   firmware/manifest.json          -> versión ACTIVA (la que descarga la app de escritorio)
//   firmware/base/*.bin             -> bootloader, tabla de particiones y selector de arranque
//   firmware/v1.0.0/manifest.json   -> copia del manifest de esa versión (para reactivarla)
//   firmware/v1.0.0/*.bin           -> la imagen publicada
//
// Con las piezas base guardadas, basta con subir el .bin que exporta el Arduino
// IDE: el panel construye aquí mismo la imagen completa que arranca también en
// una placa recién comprada.

const RUTA_MANIFEST = 'firmware/manifest.json';
const RUTA_BASE = 'firmware/base';

const PIEZAS_BASE = [
  { clave: 'bootloader', archivo: 'bootloader.bin', etiqueta: 'Bootloader', offset: '0x1000' },
  { clave: 'particiones', archivo: 'partitions.bin', etiqueta: 'Tabla de particiones', offset: '0x8000' },
  { clave: 'boot_app0', archivo: 'boot_app0.bin', etiqueta: 'Selector de arranque', offset: '0xe000' },
];

const ES_VERSION = /^v\d+\.\d+\.\d+$/;

const adivinarOffset = (nombre) => {
  const n = nombre.toLowerCase();
  if (n.includes('merged')) return '0x0';
  if (n.includes('bootloader')) return '0x1000';
  if (n.includes('partitions')) return '0x8000';
  if (n.includes('boot_app0')) return '0xe000';
  return '0x10000';
};

const formatearTamano = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

const leerManifest = async (ruta) => {
  const bytes = await getBytes(ref(storage, ruta));
  return JSON.parse(new TextDecoder().decode(bytes));
};

const leerArchivo = async (file) => new Uint8Array(await file.arrayBuffer());

const AdminFirmware = () => {
  const [manifest, setManifest] = useState(null);
  const [errorManifest, setErrorManifest] = useState('');
  const [versiones, setVersiones] = useState([]);
  const [base, setBase] = useState({}); // { bootloader: {tamano}, ... }
  const [cargando, setCargando] = useState(true);

  const [version, setVersion] = useState('');
  const [notas, setNotas] = useState('');
  const [archivos, setArchivos] = useState([]); // [{ file, bytes, info, offset }]
  const [subiendo, setSubiendo] = useState(false);
  const [subiendoBase, setSubiendoBase] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [estadoSubida, setEstadoSubida] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const cargar = async () => {
    setCargando(true);
    setErrorManifest('');
    try {
      setManifest(await leerManifest(RUTA_MANIFEST));
    } catch (err) {
      setManifest(null);
      if (err?.code === 'storage/object-not-found') {
        setErrorManifest('Todavía no hay ningún firmware publicado.');
      } else if (err?.code === 'storage/unauthorized') {
        setErrorManifest('Storage no te deja leer firmware/. Despliega las reglas actualizadas (firebase deploy --only storage).');
      } else {
        setErrorManifest(`No se pudo leer el manifest: ${err.message}`);
      }
    }
    try {
      const listado = await listAll(ref(storage, 'firmware'));
      setVersiones(listado.prefixes.map((p) => p.name).filter((n) => ES_VERSION.test(n)).sort().reverse());
    } catch {
      setVersiones([]);
    }
    try {
      const listado = await listAll(ref(storage, RUTA_BASE));
      const encontrado = {};
      listado.items.forEach((item) => {
        const pieza = PIEZAS_BASE.find((p) => p.archivo === item.name);
        if (pieza) encontrado[pieza.clave] = true;
      });
      setBase(encontrado);
    } catch {
      setBase({});
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  const baseCompleta = PIEZAS_BASE.every((p) => base[p.clave]);

  // --- Piezas base -------------------------------------------------------

  const subirPiezasBase = async (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = '';
    if (lista.length === 0) return;
    setError('');
    setOk('');
    setSubiendoBase(true);
    try {
      const subidas = [];
      for (const file of lista) {
        // eslint-disable-next-line no-await-in-loop
        const bytes = await leerArchivo(file);
        const info = analizarBinario(bytes);
        const pieza = PIEZAS_BASE.find((p) => p.clave === info.tipo);
        if (!pieza) {
          throw new Error(
            `"${file.name}" es ${info.etiqueta}; aquí solo van el bootloader, la tabla de particiones y el selector de arranque.`
          );
        }
        // eslint-disable-next-line no-await-in-loop
        await uploadBytes(ref(storage, `${RUTA_BASE}/${pieza.archivo}`), file, {
          contentType: 'application/octet-stream',
        });
        subidas.push(pieza.etiqueta);
      }
      setOk(`Piezas base guardadas: ${subidas.join(', ')}.`);
      await cargar();
    } catch (err) {
      setError(err?.code === 'storage/unauthorized' ? 'Storage rechazó la subida de las piezas base.' : err.message);
    } finally {
      setSubiendoBase(false);
    }
  };

  const descargarPiezasBase = async () => {
    const piezas = {};
    for (const p of PIEZAS_BASE) {
      // eslint-disable-next-line no-await-in-loop
      const bytes = await getBytes(ref(storage, `${RUTA_BASE}/${p.archivo}`));
      piezas[p.clave] = new Uint8Array(bytes);
    }
    return piezas;
  };

  // --- Publicación -------------------------------------------------------

  const elegirArchivos = async (e) => {
    const lista = Array.from(e.target.files || []);
    setError('');
    setOk('');
    const analizados = [];
    for (const file of lista) {
      // eslint-disable-next-line no-await-in-loop
      const bytes = await leerArchivo(file);
      analizados.push({ file, bytes, info: analizarBinario(bytes), offset: adivinarOffset(file.name) });
    }
    setArchivos(analizados);
  };

  const cambiarOffset = (indice, valor) => {
    setArchivos((prev) => prev.map((a, i) => (i === indice ? { ...a, offset: valor } : a)));
  };

  // Qué hará el botón publicar según lo que se haya seleccionado.
  const modo = (() => {
    if (archivos.length !== 1) return 'manual';
    const { tipo } = archivos[0].info;
    if (tipo === 'aplicacion') return baseCompleta ? 'combinar' : 'faltan-base';
    if (tipo === 'imagen-completa') return 'directa';
    return 'manual';
  })();

  const publicar = async () => {
    setError('');
    setOk('');
    const ver = version.trim().replace(/^v/i, '');
    if (!/^\d+\.\d+\.\d+$/.test(ver)) {
      setError('La versión debe tener el formato X.Y.Z (por ejemplo 1.0.0).');
      return;
    }
    if (archivos.length === 0) {
      setError('Selecciona al menos un archivo .bin.');
      return;
    }
    if (modo === 'faltan-base') {
      setError('Has elegido la aplicación sola. Sube antes las tres piezas base para poder construir la imagen completa.');
      return;
    }
    if (modo === 'manual') {
      for (const a of archivos) {
        if (!/^(0x[0-9a-fA-F]+|\d+)$/.test(a.offset.trim())) {
          setError(`Offset inválido para ${a.file.name}. Usa 0x10000 o un número decimal.`);
          return;
        }
      }
    }
    if (versiones.includes(`v${ver}`) && !window.confirm(`La versión v${ver} ya existe. ¿Sobrescribir sus archivos?`)) {
      return;
    }

    setSubiendo(true);
    setProgreso(0);
    const carpeta = `firmware/v${ver}`;

    try {
      let aSubir; // [{ nombre, datos: Blob|Uint8Array, tamano, offset }]

      if (modo === 'combinar') {
        setEstadoSubida('Descargando piezas base...');
        const piezas = await descargarPiezasBase();

        const hueco = tamanoParticionApp(piezas.particiones);
        const app = archivos[0].bytes;
        if (hueco && app.length > hueco) {
          throw new Error(
            `La aplicación ocupa ${formatearTamano(app.length)} y la partición solo tiene ${formatearTamano(hueco)}. Cambia el esquema de particiones o reduce el programa.`
          );
        }

        setEstadoSubida('Construyendo la imagen completa...');
        const imagen = construirImagenCompleta({
          bootloader: piezas.bootloader,
          particiones: piezas.particiones,
          bootApp0: piezas.boot_app0,
          app,
        });
        if (!verificarImagenCompleta(imagen)) {
          throw new Error('La imagen construida no pasó la comprobación. Revisa las piezas base.');
        }
        const nombre = archivos[0].file.name.replace(/\.bin$/i, '') + '.merged.bin';
        aSubir = [{ nombre, datos: imagen, tamano: imagen.length, offset: '0x0' }];
      } else {
        aSubir = archivos.map((a) => ({
          nombre: a.file.name,
          datos: a.file,
          tamano: a.file.size,
          offset: modo === 'directa' ? '0x0' : a.offset.trim(),
        }));
      }

      const totalBytes = aSubir.reduce((s, a) => s + a.tamano, 0);
      const subidoPorArchivo = aSubir.map(() => 0);
      const entradas = [];

      for (let i = 0; i < aSubir.length; i += 1) {
        const item = aSubir[i];
        setEstadoSubida(`Subiendo ${item.nombre}...`);
        const rutaArchivo = `${carpeta}/${item.nombre}`;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve, reject) => {
          const tarea = uploadBytesResumable(ref(storage, rutaArchivo), item.datos, {
            contentType: 'application/octet-stream',
          });
          tarea.on(
            'state_changed',
            (snap) => {
              subidoPorArchivo[i] = snap.bytesTransferred;
              const acumulado = subidoPorArchivo.reduce((s, v) => s + v, 0);
              setProgreso(totalBytes ? Math.round((acumulado / totalBytes) * 100) : 0);
            },
            reject,
            resolve
          );
        });
        entradas.push({ ruta: rutaArchivo, offset: item.offset });
      }

      const nuevoManifest = {
        version: ver,
        chip: 'ESP32',
        notas: notas.trim(),
        publicadoEn: new Date().toISOString(),
        origen: modo === 'combinar' ? 'combinada-en-panel' : modo,
        archivos: entradas,
      };
      const json = JSON.stringify(nuevoManifest, null, 2);

      setEstadoSubida('Publicando manifest...');
      await uploadString(ref(storage, `${carpeta}/manifest.json`), json, 'raw', { contentType: 'application/json' });
      await uploadString(ref(storage, RUTA_MANIFEST), json, 'raw', { contentType: 'application/json' });

      setOk(`Firmware v${ver} publicado. Los usuarios con ESP32 aprobado ya pueden instalarlo.`);
      setVersion('');
      setNotas('');
      setArchivos([]);
      await cargar();
    } catch (err) {
      console.error('Error publicando firmware', err);
      setError(
        err?.code === 'storage/unauthorized'
          ? 'Storage rechazó la subida. Comprueba que las reglas permiten escribir en firmware/ a los administradores.'
          : `Error publicando el firmware: ${err.message}`
      );
    } finally {
      setSubiendo(false);
      setEstadoSubida('');
      setProgreso(0);
    }
  };

  const activar = async (nombreVersion) => {
    if (!window.confirm(`¿Poner ${nombreVersion} como versión activa para todos los usuarios?`)) return;
    setError('');
    setOk('');
    try {
      const m = await leerManifest(`firmware/${nombreVersion}/manifest.json`);
      await uploadString(ref(storage, RUTA_MANIFEST), JSON.stringify(m, null, 2), 'raw', {
        contentType: 'application/json',
      });
      setOk(`${nombreVersion} es ahora la versión activa.`);
      await cargar();
    } catch (err) {
      setError(
        err?.code === 'storage/object-not-found'
          ? `${nombreVersion} no tiene manifest propio (se subió a mano). Vuelve a publicarla desde aquí.`
          : `No se pudo activar: ${err.message}`
      );
    }
  };

  const eliminar = async (nombreVersion) => {
    if (manifest && `v${manifest.version}` === nombreVersion) {
      alert('No puedes borrar la versión activa. Publica o activa otra antes.');
      return;
    }
    if (!window.confirm(`¿Borrar definitivamente ${nombreVersion} y todos sus archivos?`)) return;
    try {
      const listado = await listAll(ref(storage, `firmware/${nombreVersion}`));
      await Promise.all(listado.items.map((item) => deleteObject(item)));
      setOk(`${nombreVersion} eliminada.`);
      await cargar();
    } catch (err) {
      setError(`No se pudo borrar: ${err.message}`);
    }
  };

  const activa = manifest ? `v${manifest.version}` : null;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <HardDriveUpload size={16} className="text-indigo-400" />
        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Firmware del ESP32</span>
        {activa && (
          <span className="bg-green-500/20 text-green-400 text-[10px] px-2 py-0.5 rounded border border-green-500/30 font-black uppercase tracking-wider">
            activa {activa}
          </span>
        )}
        <button
          onClick={cargar}
          className="ml-auto p-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white border border-slate-600"
          title="Recargar"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="bg-slate-700/30 rounded-xl border border-slate-600 p-4">
        {cargando ? (
          <div className="text-slate-500 text-sm">Cargando...</div>
        ) : (
          <>
            {manifest ? (
              <div className="mb-4 text-sm">
                <div className="flex items-center gap-2 text-green-400 font-bold">
                  <CheckCircle2 size={16} /> v{manifest.version}
                  {manifest.notas && <span className="text-slate-400 font-normal">· {manifest.notas}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono">
                  {manifest.archivos?.map((a) => `${a.ruta.split('/').pop()} @ ${a.offset}`).join(' · ')}
                </div>
              </div>
            ) : (
              <div className="mb-4 text-sm text-yellow-400 flex items-center gap-2">
                <AlertTriangle size={16} /> {errorManifest}
              </div>
            )}

            {error && <div className="mb-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}
            {ok && <div className="mb-3 text-sm text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg p-2">{ok}</div>}

            {/* Piezas base */}
            <div className="border-t border-slate-600/40 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={13} className="text-indigo-400" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Piezas base</span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded border font-black uppercase tracking-wider ${
                    baseCompleta
                      ? 'bg-green-500/10 text-green-400 border-green-500/20'
                      : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                  }`}
                >
                  {baseCompleta ? 'listas' : 'faltan'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Se suben una sola vez. No cambian con tu programa, solo si actualizas el core de ESP32 en el
                Arduino IDE. Con ellas guardadas, para publicar te basta el .bin que exporta el IDE.
              </p>
              <div className="grid sm:grid-cols-3 gap-2 mb-3">
                {PIEZAS_BASE.map((p) => (
                  <div
                    key={p.clave}
                    className={`rounded-lg border p-2 text-xs ${
                      base[p.clave] ? 'border-green-500/30 bg-green-500/5' : 'border-slate-600 bg-slate-900/40'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-bold text-slate-300">
                      {base[p.clave] ? <CheckCircle2 size={12} className="text-green-400" /> : <AlertTriangle size={12} className="text-yellow-400" />}
                      {p.etiqueta}
                    </div>
                    <div className="text-slate-500 font-mono mt-0.5">{p.archivo} @ {p.offset}</div>
                  </div>
                ))}
              </div>
              <input
                type="file"
                accept=".bin"
                multiple
                onChange={subirPiezasBase}
                disabled={subiendoBase || subiendo}
                className="w-full text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-600 file:text-white hover:file:bg-slate-500 file:cursor-pointer disabled:opacity-50"
              />
              {subiendoBase && <div className="text-xs text-slate-400 mt-1">Subiendo piezas base...</div>}
            </div>

            {/* Publicar nueva versión */}
            <div className="border-t border-slate-600/40 pt-4 mt-4">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Publicar nueva versión</div>

              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Versión</label>
                  <input
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="1.0.0"
                    disabled={subiendo}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Notas (opcional)</label>
                  <input
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Qué cambia en esta versión"
                    disabled={subiendo}
                    className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
                  />
                </div>
              </div>

              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                Archivo .bin (Arduino IDE → Sketch → Exportar binario compilado)
              </label>
              <input
                type="file"
                accept=".bin"
                multiple
                onChange={elegirArchivos}
                disabled={subiendo}
                className="w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-500 file:cursor-pointer disabled:opacity-50"
              />

              {archivos.length > 0 && (
                <div className="mt-3 grid gap-2">
                  {archivos.map((a, i) => (
                    <div key={a.file.name} className="flex items-center gap-3 bg-slate-900/40 border border-slate-600 rounded-lg p-2">
                      <FileCode2 size={16} className="text-indigo-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{a.file.name}</div>
                        <div className="text-xs text-slate-500">
                          {formatearTamano(a.file.size)} · {a.info.etiqueta}
                          {a.info.app?.fecha ? ` · compilado ${a.info.app.fecha}` : ''}
                        </div>
                      </div>
                      {modo === 'manual' && (
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] font-bold text-slate-500 uppercase">Offset</span>
                          <input
                            value={a.offset}
                            onChange={(e) => cambiarOffset(i, e.target.value)}
                            disabled={subiendo}
                            className="w-24 bg-slate-900/60 border border-slate-600 rounded px-2 py-1 text-white text-sm font-mono focus:outline-none focus:border-blue-500 disabled:opacity-50"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {modo === 'combinar' && (
                <div className="mt-3 text-sm text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 flex items-start gap-2">
                  <Wand2 size={16} className="shrink-0 mt-0.5" />
                  <span>
                    Se combinará con las piezas base para crear la imagen completa, que arranca también en una
                    placa nueva. No tienes que hacer nada más.
                  </span>
                </div>
              )}
              {modo === 'directa' && (
                <div className="mt-3 text-sm text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-lg p-2">
                  Ya es una imagen completa: se publicará tal cual en el offset 0x0.
                </div>
              )}
              {modo === 'faltan-base' && (
                <div className="mt-3 text-sm text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-2">
                  Has elegido la aplicación sola. Sube primero las tres piezas base, aquí arriba, y entonces el
                  panel podrá construir la imagen completa.
                </div>
              )}

              {subiendo && (
                <div className="mt-3">
                  <div className="h-2 bg-slate-900 rounded-full overflow-hidden border border-slate-600">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all" style={{ width: `${progreso}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>{estadoSubida}</span>
                    <span>{progreso}%</span>
                  </div>
                </div>
              )}

              <button
                onClick={publicar}
                disabled={subiendo || archivos.length === 0 || modo === 'faltan-base'}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {subiendo ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
                Publicar firmware
              </button>
            </div>

            {versiones.length > 0 && (
              <div className="border-t border-slate-600/40 pt-4 mt-4">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Versiones en la nube</div>
                <div className="grid gap-2">
                  {versiones.map((v) => (
                    <div key={v} className="flex items-center justify-between gap-2 bg-slate-900/40 border border-slate-600 rounded-lg px-3 py-2">
                      <span className="font-mono text-sm text-slate-300">
                        {v}
                        {v === activa && <span className="ml-2 text-[10px] text-green-400 font-bold uppercase">activa</span>}
                      </span>
                      <div className="flex items-center gap-2">
                        {v !== activa && (
                          <button
                            onClick={() => activar(v)}
                            className="px-3 py-1 rounded text-xs font-bold bg-slate-600/30 text-slate-300 border border-slate-600 hover:bg-green-500/10 hover:text-green-400 hover:border-green-500/30"
                          >
                            Activar
                          </button>
                        )}
                        <button
                          onClick={() => eliminar(v)}
                          disabled={v === activa}
                          className="p-1.5 rounded bg-slate-700 text-red-400 hover:bg-red-500/20 border border-transparent hover:border-red-500/30 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={v === activa ? 'No se puede borrar la versión activa' : 'Borrar versión'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminFirmware;
